import React, { useState } from "react";
import { Button } from "components/Graphs/Button";
import agent from "server";

import { Input } from "components/Graphs/Input";
import { Switch } from "components/Graphs/Switch";
import { Textarea } from "components/Graphs/TextArea";
import { Loader2, Info } from "lucide-react";
import { CopyButton, FlatCard } from "components/UI-lib";
import { DatePicker } from "components/Graphs/DateRangePicker";

// --- Mock API function, moved from the API route ---
interface CreateCouponCodeRequest {
  code: string;
  amount: number;
  maxRedemptions: number;
  description?: string;
  expiresAt?: string;
  maxRedemptionsPerTenant?: number;
}

export default function CreateCreditCode() {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [code, setCode] = useState("");
  const [amount, setAmount] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [description, setDescription] = useState("");
  const [maxRedemptionsPerTenant, setMaxRedemptionsPerTenant] = useState("1");
  const [errorMessage, setErrorMessage] = useState("");
  const [successCode, setSuccessCode] = useState("");

  const handleCreateCoupon = async () => {
    setErrorMessage("");
    setSuccessCode("");
    // Custom Validation
    if (!code.trim()) {
      setErrorMessage("Credit Code Name is required.");
      return;
    }
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setErrorMessage("Amount must be a positive number.");
      return;
    }
    const numericMaxRedemptions = parseInt(maxRedemptions, 10);
    if (isNaN(numericMaxRedemptions) || numericMaxRedemptions <= 0) {
      setErrorMessage(
        "Global Max Redemptions must be a positive whole number."
      );
      return;
    }
    if (isAdvanced) {
      const numericTenantRedemptions = parseInt(maxRedemptionsPerTenant, 10);
      if (isNaN(numericTenantRedemptions) || numericTenantRedemptions <= 0) {
        setErrorMessage(
          "Max Redemptions Per Tenant must be a positive whole number."
        );
        return;
      }
    }

    setIsLoading(true);

    const requestBody = {
      code,
      amount: parseFloat(amount),
      maxRedemptions: parseInt(maxRedemptions, 10),
      description,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : "",
      maxRedemptionsPerTenant: isAdvanced
        ? parseInt(maxRedemptionsPerTenant, 10)
        : 1,
    };

    const res = await agent.Admin.createCouponCode(requestBody);

    if (res && res.data) {
      setSuccessCode(res.data?.shortCode);
      // Reset form
      setCode("");
      setAmount("");
      setMaxRedemptions("");
      setExpiresAt("");
      setDescription("");
      setMaxRedemptionsPerTenant("1");
      setIsAdvanced(false);
    } else {
      setErrorMessage(`Failed to create: ${res.message}`);
    }
    setIsLoading(false);
  };

  return (
    <FlatCard className="w-full">
      <div>
        <h2 className="text-xl font-bold text-primary">Create Credit Code</h2>
        <p className="text-sm text-secondary">
          Fill in the details to generate a new credit code.
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-end space-x-2">
            <label htmlFor="advanced-mode" className="text-sm text-secondary">
              Advanced
            </label>
            <Switch
              id="advanced-mode"
              checked={isAdvanced}
              onCheckedChange={setIsAdvanced}
            />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="code" className="text-sm text-secondary">
                Credit Code Name
              </label>
              <Input
                id="code"
                placeholder="e.g., SUMMER25"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="amount" className="text-sm text-secondary">
                Amount (USD)
              </label>
              <Input
                id="amount"
                type="number"
                placeholder="e.g., 25.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="maxRedemptions" className="text-sm text-secondary">
              Global Max Redemptions
            </label>
            <Input
              id="maxRedemptions"
              type="number"
              placeholder="e.g., 1000"
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
            />
          </div>

          {isAdvanced && (
            <div className="space-y-2 rounded-lg border border-zinc-800 bg-secondaryBg p-4">
              <label
                htmlFor="maxRedemptionsPerTenant"
                className="text-sm text-secondary"
              >
                Max Redemptions Per User
              </label>
              <Input
                id="maxRedemptionsPerTenant"
                type="number"
                placeholder="e.g., 1"
                value={maxRedemptionsPerTenant}
                onChange={(e) => setMaxRedemptionsPerTenant(e.target.value)}
              />
              <p className="flex items-start gap-2 pt-2 text-sm text-muted-foreground">
                <Info className="h-4 w-4 flex-shrink-0 translate-y-0.5" />
                <span>
                  <strong>Global Max Redemptions</strong> is the total number of
                  times the coupon can be used across all users. <br />
                  <strong>Max Redemptions Per User</strong> is the number of
                  times a single user can use this credit code (defaulted to 1).
                </span>
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <div className="flex flex-row items-center">
                <label htmlFor="expiresAt" className="text-sm text-secondary">
                  Expires At (Optional)
                </label>
                <div className="flex items-center">
                  {expiresAt && (
                    <Button
                      variant="secondary"
                      className="h-7 ml-2"
                      onClick={() => setExpiresAt("")}
                    >
                      Reset Date
                    </Button>
                  )}
                </div>
              </div>
              <DatePicker
                id="expiresAt"
                value={expiresAt ? new Date(expiresAt) : undefined}
                onChange={(date) => setExpiresAt(date?.toISOString() || "")}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm text-secondary">
                Description (Optional)
              </label>
              <Textarea
                id="description"
                placeholder="e.g., for nvidia hackakon | attatched to vss launchable"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[40px]"
              />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Button
            type="button"
            onClick={handleCreateCoupon}
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Coupon"
            )}
          </Button>
        </div>
      </div>
      <div className="mt-2">
        {errorMessage && <p className="text-red-500">{errorMessage}</p>}
        {successCode && (
          <div className="flex flex-row items-center gap-2 mt-6">
            <p className="text-green-500 text-sm mr-1">
              Credit Code created successfully:
            </p>
            <div className="flex flex-row border border-zinc-800 rounded-md p-1">
              <CopyButton
                value={successCode}
                className="text-primary text-sm"
              />
            </div>
          </div>
        )}
      </div>
    </FlatCard>
  );
}
